import { Autocomplete, CircularProgress } from '@mui/material';
import TextField from '@mui/material/TextField';
import React, { Fragment, useEffect, useState } from 'react';
import { useLanguageContextAPI } from '../../../shared/context/LanguageContext';
import { getAllHtz } from '../service/HtzService';

interface PartSearchDropdownProps {
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  screenGridId: number;
}

function debounce(duration: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

const PartSearchDropdown = ({ setSearchValue, screenGridId }: PartSearchDropdownProps) => {
  const [value, setValue] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<{ htz: string }[] | []>([]);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(open && options.length === 0);

  const { t } = useLanguageContextAPI();

  useEffect(() => {
    let active = true;

    if (!loading) return;

    async function fetchFn() {
      // async 1
      await debounce(1000);
      // async 2
      if (active) {
        const htzData: { npi_qry_search: { htz: string }[] } = await getAllHtz(
          inputValue,
          screenGridId,
        );
        if (htzData?.['npi_qry_search']) setOptions(htzData?.['npi_qry_search'].slice(0, 20));
        setLoading(false);
      }
    }
    fetchFn();

    return () => {
      active = false;
    };
  }, [loading, inputValue]);

  // clear options on dropdown close
  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <>
      <Autocomplete
        sx={{ width: 300 }}
        value={value}
        inputValue={inputValue}
        open={open}
        onOpen={() => {
          setOpen(true);
          setLoading(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={(event: React.SyntheticEvent<Element, Event>, newValue: string) => {
          setValue(newValue);
          setSearchValue(newValue);
        }}
        onInputChange={(event: React.SyntheticEvent<Element, Event>, newInputValue: string) => {
          setInputValue(newInputValue);
          setLoading(true);
        }}
        disablePortal
        options={options.map((option) => option?.htz)}
        noOptionsText={t('part_search.no_options')}
        loading={loading}
        clearIcon={null}
        style={{ marginLeft: '-5px', marginBlock : '10px' }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='HTZ Value'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              ),
            }}
          />
        )}
      />
    </>
  );
};

export default PartSearchDropdown;
